// The value side of custom fields — encoding for the API and the two validity checks.
//
// Split out of CustomFields.jsx so that modules with no JSX in them (child-tickets.js,
// and the node tests that pin it) can import the conversion without importing a
// component: node cannot load a .jsx file. CustomFields.jsx re-exports these, so every
// existing import from it still holds. The rules themselves are documented in that
// file's header: UI state is strings (and arrays), the API is typed, and this is the
// single place the conversion happens.

export function encodeCustomFields(formFields, values) {
  const payload = {};

  for (const field of formFields) {
    const raw = values[field.key];

    switch (field.data_type) {
      case 'checkbox':
        // A key absent means no row (docs/API.md §1.9); writing "false" rows for
        // every unticked box on every ticket is noise in the value table.
        if (raw === true) payload[field.key] = true;
        break;
      case 'multiselect':
        if (Array.isArray(raw) && raw.length) payload[field.key] = raw;
        break;
      case 'number': {
        const trimmed = String(raw ?? '').trim();
        if (trimmed !== '') payload[field.key] = Number(trimmed);
        break;
      }
      default: {
        const trimmed = String(raw ?? '').trim();
        if (trimmed !== '') payload[field.key] = trimmed;
        break;
      }
    }
  }

  return payload;
}

/** Required fields with nothing in them, as `{ key, label }` for the error summary. */
export function missingRequiredFields(formFields, values) {
  return formFields
    .filter((field) => {
      if (!field.is_required) return false;
      // A checkbox always has a value, so "required" cannot mean "must be ticked"
      // without turning an optional flag into a consent gate. Treat it as answered.
      if (field.data_type === 'checkbox') return false;
      const raw = values[field.key];
      if (Array.isArray(raw)) return raw.length === 0;
      return String(raw ?? '').trim() === '';
    })
    .map((field) => ({ key: field.key, label: field.label }));
}

export function invalidNumberFields(formFields, values) {
  return formFields
    .filter((field) => field.data_type === 'number')
    .filter((field) => {
      const trimmed = String(values[field.key] ?? '').trim();
      return trimmed !== '' && !Number.isFinite(Number(trimmed));
    })
    .map((field) => ({ key: field.key, label: field.label }));
}
